import { PHONE_FIELDS } from './phone-columns';

/**
 * Метаописание колонок грида — единый источник истины для фронта и бэка.
 *
 * Фронтенд строит по нему колонки TanStack Table и выбирает редактор ячейки.
 * Бэкенд использует его как белый список: что можно сортировать, фильтровать
 * и редактировать. Никаких «прокинуть имя поля из запроса прямо в ORDER BY».
 *
 * Решение Р-02: набор колонок фиксирован и меняется только через релиз.
 */

export type ColumnType =
  | 'text' // однострочный текст
  | 'longtext' // многострочный (комментарий) — раскрывающийся редактор
  | 'phone' // телефон с маской и нормализацией
  | 'date' // дата, ввод дд.мм.гггг + календарь
  | 'number' // целое число
  | 'boolean' // чекбокс
  | 'dict' // значение справочника, комбобокс с поиском
  | 'user' // ссылка на сотрудника (ответственный)
  | 'tags' // множественные метки
  | 'files' // документы кандидата (превью)
  | 'computed'; // вычисляется системой, руками не правится

/** Справочники, на которые ссылаются колонки типа dict. */
export type DictionaryName =
  | 'cities'
  | 'statuses'
  | 'workObjects'
  | 'sources'
  | 'projects'
  | 'travelGroups';

export interface ColumnMeta {
  /** Ключ поля в API и в объекте кандидата */
  key: string;
  /** Заголовок в шапке грида */
  label: string;
  /** Подсказка при наведении — объясняет, что это за поле */
  hint?: string;
  type: ColumnType;
  /** Справочник для type: 'dict' */
  dictionary?: DictionaryName;
  /**
   * Каскад: значение этой колонки фильтрует список справочника.
   * Реализация решения Р-08 — «Город» зависит от «Гражданства».
   */
  dependsOn?: string;
  /** Ширина по умолчанию, px */
  width: number;
  minWidth?: number;
  /**
   * Верхний предел ширины.
   *
   * Нужен колонкам, где в ячейке не текст, а значок: «Ведёт» показывает
   * кружок с инициалами, и растягивать её на полторы сотни пикселей
   * бессмысленно — место отбирается у данных в каждой строке.
   */
  maxWidth?: number;
  editable: boolean;
  sortable: boolean;
  filterable: boolean;
  /** Показывать в гриде по умолчанию (остальные — через «Колонки») */
  visibleByDefault: boolean;
  /** Составное поле для хранения/истории; не является отдельной колонкой интерфейса. */
  internal?: boolean;
  /** Закрепить слева при горизонтальной прокрутке */
  pinned?: 'left';
  /** Группа в панели управления колонками */
  group: 'main' | 'contacts' | 'work' | 'passport' | 'extra' | 'system';
  /** Обязательное при создании кандидата */
  required?: boolean;
  align?: 'left' | 'center' | 'right';
}

/*
  Порядок колонок повторяет рабочую таблицу.

  Решение заказчика: сначала двенадцать колонок листа «2026г» в том же
  порядке, в каком он их читает годами (Проект · № · ФИО · телефоны ·
  дата рождения · гражданство · где общение · город · статус · объект
  работы · заметки), а следом всё, чего в таблице не было.

  «Ведёт» и «Едут вместе» в таблице колонками не были вовсе — они жили
  цветом, — поэтому стоят после двенадцати. У «Ведёт» заодно снято
  закрепление слева: закреплённая колонка физически переезжает в левый
  край, и порядок листа она бы просто перебила.
*/
export const CANDIDATE_COLUMNS: readonly ColumnMeta[] = [
  // ── Основное ───────────────────────────────────────────────
  {
    key: 'projectId',
    label: 'Пр.',
    hint: 'Проект — по чьей заявке набираем. В ячейке первая буква названия, полное — при наведении. В старой таблице — первая колонка без заголовка',
    type: 'dict',
    dictionary: 'projects',
    // Под букву, а не под название: проектов пять, они повторяются в сотнях
    // строк подряд и различаются цветом раньше, чем дочитывается название,
    // а 130 пикселей отбирались у данных в каждой строке — как было
    // с именем в колонке «Ведёт»
    width: 56,
    minWidth: 44,
    maxWidth: 72,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    pinned: 'left',
    group: 'main',
    align: 'center',
  },
  {
    key: 'rowNumber',
    label: '№',
    type: 'computed',
    width: 64,
    editable: false,
    sortable: true,
    filterable: false,
    visibleByDefault: true,
    pinned: 'left',
    group: 'system',
    align: 'right',
  },
  {
    key: 'fullName',
    label: 'ФИО',
    type: 'text',
    width: 260,
    minWidth: 160,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    pinned: 'left',
    group: 'main',
    required: true,
  },

  // ── Контакты ───────────────────────────────────────────────
  ...PHONE_FIELDS.map<ColumnMeta>((field) => ({
    key: field.rawField,
    label: field.label,
    type: 'phone',
    width: field.country === 'OTHER' ? 185 : 165,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: field.visibleByDefault,
    group: 'contacts',
  })),
  {
    key: 'birthDate',
    label: 'Дата рождения',
    type: 'date',
    width: 130,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'main',
    align: 'center',
  },
  {
    key: 'age',
    label: 'Возраст',
    hint: 'Полных лет на сегодня — рассчитывается по дате рождения',
    type: 'number',
    width: 80,
    minWidth: 64,
    editable: false,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'main',
    align: 'center',
  },
  {
    key: 'citizenship',
    label: 'Гр-во',
    hint: 'Гражданство. Определяет, города какой страны предлагаются в колонке «Город»',
    type: 'dict',
    width: 90,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'main',
    align: 'center',
  },
  {
    key: 'communicationRaw',
    label: 'Где общение?',
    hint: 'Мессенджер и рабочая линия — пока как в старой таблице, одной строкой',
    type: 'text',
    width: 150,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'contacts',
  },
  {
    key: 'cityId',
    label: 'Город',
    hint: 'Начните вводить название города',
    type: 'dict',
    dictionary: 'cities',
    width: 160,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'main',
  },
  {
    key: 'statusId',
    label: 'Статус',
    type: 'dict',
    dictionary: 'statuses',
    width: 150,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'main',
  },
  {
    key: 'workObjectId',
    label: 'Объект',
    hint: 'Объект из каталога и свободное дополнение рядом: смены и договорённости',
    type: 'dict',
    dictionary: 'workObjects',
    width: 250,
    minWidth: 170,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'work',
  },
  {
    // Составная ячейка «Объект» показывает этот текст рядом со справочником.
    // Метаданные нужны серверному белому списку и истории, отдельно колонку не выводим.
    key: 'workObjectNote',
    label: 'Дополнение к объекту',
    type: 'longtext',
    width: 250,
    editable: true,
    sortable: false,
    filterable: false,
    visibleByDefault: false,
    internal: true,
    group: 'work',
  },
  {
    key: 'workNote',
    label: 'Объект работы, кол-во смен',
    hint: 'Количество смен и договорённости — свободный текст из прежней таблицы',
    type: 'longtext',
    width: 320,
    minWidth: 180,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: false,
    group: 'work',
  },
  {
    // Двенадцатая колонка листа — заметки о звонках («2.07 не отв»).
    // В таблице она была без заголовка и сразу за объектом работы,
    // поэтому и здесь стоит тут же, а не в конце среди прочего
    key: 'comment',
    label: 'Комментарий',
    hint: 'Заметки о звонках. В старой таблице — последняя колонка без заголовка',
    type: 'longtext',
    width: 300,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: true,
    group: 'extra',
  },

  // ── Дальше то, чего в рабочей таблице колонками не было ────
  {
    key: 'responsibleUserId',
    label: 'Ведёт',
    hint: 'Кто ведёт кандидата. В старой таблице — цвет ячейки с номером строки',
    type: 'user',
    // Под кружок с инициалами, а не под имя: имя целиком в ячейку всё равно
    // не помещалось, а 120 пикселей отбирались у данных в каждой строке
    width: 56,
    minWidth: 44,
    maxWidth: 72,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'main',
    align: 'center',
  },
  {
    key: 'travelGroupId',
    label: 'Едут вместе',
    hint: 'Кто едет одной машиной. Отмечается цветом: выделите строки и выберите цвет',
    type: 'dict',
    dictionary: 'travelGroups',
    width: 130,
    editable: true,
    sortable: true,
    filterable: true,
    /*
      Колонкой не показываем.

      Кто с кем едет, видно цветом: заливка фамилии и полоса слева — так
      же, как отмечали заливкой в рабочей таблице. Колонка с названием
      группы повторяет тот же факт словами и стоит 130 пикселей ширины,
      одинаковых у всех соседних строк. Состав группы поимённо — вопрос
      к карточке, там он и показан.

      Кому названия всё-таки нужны — включает колонку руками.
    */
    visibleByDefault: false,
    group: 'main',
  },
  {
    key: 'documentsCount',
    label: 'Документы',
    hint: 'Загруженные сканы паспорта и других документов',
    type: 'files',
    width: 110,
    editable: false,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'main',
    align: 'center',
  },

  // ── Работа: дополнительные структурированные поля ────────
  {
    key: 'shiftsCount',
    label: 'Смен',
    type: 'number',
    width: 80,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: false,
    group: 'work',
    align: 'right',
  },
  {
    key: 'isBooked',
    label: 'Бронь',
    type: 'boolean',
    width: 80,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: false,
    group: 'work',
    align: 'center',
  },
  {
    key: 'plannedDepartureDate',
    label: 'Дата выезда',
    hint: 'Редактирование меняет плановую дату; фактическая сохраняется',
    type: 'date',
    width: 260,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: false,
    group: 'work',
    align: 'center',
  },

  // ── Паспорт ────────────────────────────────────────────────
  {
    key: 'passportSeries',
    label: 'Серия паспорта',
    type: 'text',
    width: 130,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'passport',
  },
  {
    key: 'passportNumber',
    label: 'Номер паспорта',
    type: 'text',
    width: 140,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'passport',
  },
  {
    key: 'passportIssuedBy',
    label: 'Кем выдан',
    type: 'longtext',
    width: 260,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'passport',
  },
  {
    key: 'passportIssueDate',
    label: 'Дата выдачи',
    type: 'date',
    width: 130,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'passport',
    align: 'center',
  },
  {
    key: 'passportPersonalNumber',
    label: 'Личный номер (РБ)',
    hint: 'Идентификационный номер в паспорте гражданина Беларуси',
    type: 'text',
    width: 160,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'passport',
  },
  {
    key: 'passportDivisionCode',
    label: 'Код подразделения (РФ)',
    type: 'text',
    width: 170,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'passport',
  },
  {
    key: 'registrationAddress',
    label: 'Адрес регистрации',
    type: 'longtext',
    width: 280,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'passport',
  },

  // ── Дополнительно ──────────────────────────────────────────
  {
    key: 'gender',
    label: 'Пол',
    type: 'dict',
    width: 80,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: false,
    group: 'extra',
    align: 'center',
  },
  {
    key: 'inn',
    label: 'ИНН',
    type: 'text',
    width: 130,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'extra',
  },
  {
    key: 'snils',
    label: 'СНИЛС',
    type: 'text',
    width: 140,
    editable: true,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'extra',
  },
  {
    key: 'hasMedicalBook',
    label: 'Медкнижка',
    type: 'boolean',
    width: 100,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: false,
    group: 'extra',
    align: 'center',
  },
  {
    key: 'sourceId',
    label: 'Источник',
    hint: 'Откуда пришёл кандидат',
    type: 'dict',
    dictionary: 'sources',
    width: 150,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: false,
    group: 'extra',
  },
  {
    key: 'tags',
    label: 'Метки',
    hint: 'Редактируются в карточке кандидата',
    type: 'tags',
    // Правка меток из ячейки не поддержана: это связь многие-ко-многим,
    // а не поле строки. Пометка editable здесь означала бы кнопку,
    // которая открывает редактор и отвечает отказом.
    editable: false,
    width: 200,
    sortable: false,
    filterable: true,
    visibleByDefault: false,
    group: 'extra',
  },

  // ── Системное ──────────────────────────────────────────────
  {
    key: 'addedDate',
    label: 'Добавлен',
    hint: 'Дата добавления. Заменяет строки-разделители «6 августа» из старой таблицы',
    type: 'date',
    width: 120,
    editable: true,
    sortable: true,
    filterable: true,
    visibleByDefault: true,
    group: 'system',
    align: 'center',
  },
  {
    key: 'updatedAt',
    label: 'Изменён',
    type: 'computed',
    width: 150,
    editable: false,
    sortable: true,
    filterable: true,
    visibleByDefault: false,
    group: 'system',
    align: 'center',
  },
  {
    key: 'updatedBy',
    label: 'Кто изменил',
    type: 'user',
    width: 140,
    editable: false,
    sortable: true,
    filterable: true,
    visibleByDefault: false,
    group: 'system',
  },
] as const;

export const COLUMN_BY_KEY: Record<string, ColumnMeta> = Object.fromEntries(
  CANDIDATE_COLUMNS.map((c) => [c.key, c]),
);

export const COLUMN_GROUP_LABELS: Record<ColumnMeta['group'], string> = {
  main: 'Основное',
  contacts: 'Контакты',
  work: 'Работа и выезд',
  passport: 'Паспорт',
  extra: 'Дополнительно',
  system: 'Системные',
};

/** Белые списки для бэкенда — защита от инъекции имени поля в SQL. */
export const EDITABLE_COLUMNS: readonly string[] = CANDIDATE_COLUMNS.filter((c) => c.editable).map(
  (c) => c.key,
).concat('actualDepartureDate');
export const SORTABLE_COLUMNS: readonly string[] = CANDIDATE_COLUMNS.filter((c) => c.sortable).map(
  (c) => c.key,
).concat('sectionId', 'actualDepartureDate');
export const FILTERABLE_COLUMNS: readonly string[] = CANDIDATE_COLUMNS.filter(
  (c) => c.filterable,
).map((c) => c.key).concat('actualDepartureDate', 'resolvedGender');
/**
 * Поля, закрытые правом `document.view`.
 *
 * Сервер обнуляет их у тех, кому паспортные данные не положены (Р-11).
 * Список нужен и на клиенте: без него карточка честно рисовала раздел
 * «Паспорт» из семи прочерков — данные закрыты, а раскладка объявляет,
 * что они существуют и просто пусты.
 */
export const DOCUMENT_ONLY_COLUMNS: readonly string[] = [
  'passportSeries',
  'passportNumber',
  'passportIssuedBy',
  'passportIssueDate',
  'passportPersonalNumber',
  'passportDivisionCode',
  'registrationAddress',
  'inn',
  'snils',
];

/**
 * Что можно покрасить вручную.
 *
 * Ровно то же, что можно править: заливкой отмечают работу с данными —
 * «этот телефон проверить», «здесь ошибка в дате». Красить номер строки,
 * счётчик документов или «кто изменил» незачем — их не правят, а значит
 * и отмечать в них нечего.
 */
export const COLORABLE_COLUMNS: readonly string[] = EDITABLE_COLUMNS;

export function isColorableColumn(key: string): boolean {
  return COLORABLE_COLUMNS.includes(key);
}

export const DEFAULT_VISIBLE_COLUMNS: readonly string[] = CANDIDATE_COLUMNS.filter(
  (c) => c.visibleByDefault,
).map((c) => c.key);

export function isEditableColumn(key: string): boolean {
  return EDITABLE_COLUMNS.includes(key);
}
export function isSortableColumn(key: string): boolean {
  return SORTABLE_COLUMNS.includes(key);
}
export function isFilterableColumn(key: string): boolean {
  return FILTERABLE_COLUMNS.includes(key);
}

/**
 * Поля, по которым имеет смысл группировать таблицу.
 *
 * Список общий на клиент и сервер намеренно: по нему грид строит меню,
 * а сервер проверяет `groupBy` в запросе перемещения строки. Две копии
 * разошлись бы на первом же новом поле, и перетаскивание в новой
 * группировке начало бы отвечать 400 без внятной причины.
 *
 * Подпись — в родительном падеже: она подставляется в «Группировка ·
 * по <подпись>».
 */
export const GROUPABLE_FIELDS = [
  { key: 'sectionId', label: 'группам планового выезда' },
  { key: 'addedDate', label: 'дате добавления' },
  { key: 'plannedDepartureDate', label: 'плановой дате выезда' },
  { key: 'statusId', label: 'статусу' },
  { key: 'responsibleUserId', label: 'ответственному' },
  { key: 'travelGroupId', label: 'группе выезда' },
  { key: 'cityId', label: 'городу' },
] as const;

export const GROUPABLE_FIELD_KEYS: readonly string[] = GROUPABLE_FIELDS.map((f) => f.key);
